"use client"
import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs"
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar"
import { ThumbsUp, ThumbsDown, MessageSquare, Edit, Package, Plus, Trash2 } from "lucide-react"
import { Textarea } from "../../ui/textarea"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import useMaterialsResources from "../../../hooks/useMaterialsResources"

const MaterialsResourcesModule = ({ actionId, action }) => {
  console.log("MaterialsResourcesModule rendered with actionId:", actionId)

  const {
    materials,
    suggestions,
    comments,
    newSuggestion,
    newComment,
    activeTab,
    loading,
    setActiveTab,
    setNewComment,
    handleAddMaterial,
    handleRemoveMaterial,
    handleMaterialChange,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote,
  } = useMaterialsResources(actionId, action)

  console.log("MaterialsResourcesModule hook data:", {
    materialsCount: materials.length,
    suggestionsCount: suggestions.length,
    commentsCount: comments.length,
    loading,
  })

  if (loading) {
    return <div className="p-4">Loading materials and resources data...</div>
  }

  return (
    <div>
      <CardHeader className="px-0 pt-0">
        <CardTitle>Materials & Resources</CardTitle>
      </CardHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="current">Current</TabsTrigger>
          <TabsTrigger value="suggestions">Suggestions ({suggestions.length})</TabsTrigger>
          <TabsTrigger value="discussion">Discussion ({comments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="current">
          <Card>
            <CardContent className="p-4">
              {materials.length === 0 ? (
                <div className="text-center py-6">
                  <Package className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                  <p className="text-muted-foreground">No materials or resources have been identified yet.</p>
                  <Button onClick={() => setActiveTab("suggestions")} variant="outline" className="mt-4">
                    <Edit className="mr-2 h-4 w-4" /> Suggest Materials & Resources
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Notes
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {materials.map((item, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm">{item.name}</td>
                            <td className="px-4 py-2 text-sm capitalize">{item.type}</td>
                            <td className="px-4 py-2 text-sm">{item.quantity}</td>
                            <td className="px-4 py-2 text-sm">{item.notes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <Button onClick={() => setActiveTab("suggestions")} variant="outline">
                      <Edit className="mr-2 h-4 w-4" /> Suggest Edit
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suggestions">
          <Card className="mb-4">
            <CardContent className="p-4">
              <h3 className="text-lg font-medium mb-4">Suggest Materials & Resources</h3>

              <div className="space-y-4">
                {newSuggestion.map((item, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Item #{index + 1}</h4>
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMaterial(index)}
                          className="text-red-500 h-6 w-6 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor={`name-${index}`}>Name</Label>
                        <Input
                          id={`name-${index}`}
                          value={item.name}
                          onChange={(e) => handleMaterialChange(index, "name", e.target.value)}
                          placeholder="Material or resource name"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`type-${index}`}>Type</Label>
                        <select
                          id={`type-${index}`}
                          value={item.type}
                          onChange={(e) => handleMaterialChange(index, "type", e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="material">Material</option>
                          <option value="tool">Tool</option>
                          <option value="equipment">Equipment</option>
                          <option value="human">Human Resource</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor={`quantity-${index}`}>Quantity</Label>
                        <Input
                          id={`quantity-${index}`}
                          value={item.quantity}
                          onChange={(e) => handleMaterialChange(index, "quantity", e.target.value)}
                          placeholder="How many/much is needed"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`notes-${index}`}>Notes</Label>
                        <Input
                          id={`notes-${index}`}
                          value={item.notes}
                          onChange={(e) => handleMaterialChange(index, "notes", e.target.value)}
                          placeholder="Additional information"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <Button type="button" variant="outline" onClick={handleAddMaterial} className="w-full">
                  <Plus className="h-4 w-4 mr-2" /> Add Another Item
                </Button>

                <div className="flex justify-end">
                  <Button onClick={handleSubmitSuggestion}>Submit Suggestion</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {suggestions.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No suggestions yet. Be the first to suggest materials and resources!
            </p>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion) => (
                <Card key={suggestion.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar>
                        <AvatarImage src={suggestion.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{suggestion.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{suggestion.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(suggestion.createdAt).toLocaleString()}
                          </span>
                        </div>

                        <div className="mb-4">
                          {suggestion.materials && suggestion.materials.length > 0 && (
                            <div className="border rounded-md overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Name
                                    </th>
                                    <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Type
                                    </th>
                                    <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Quantity
                                    </th>
                                    <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Notes
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {suggestion.materials.map((item, index) => (
                                    <tr key={index}>
                                      <td className="px-3 py-1 text-xs">{item.name}</td>
                                      <td className="px-3 py-1 text-xs capitalize">{item.type}</td>
                                      <td className="px-3 py-1 text-xs">{item.quantity}</td>
                                      <td className="px-3 py-1 text-xs">{item.notes}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "up")}
                            className={suggestion.userVote === "up" ? "bg-green-50" : ""}
                          >
                            <ThumbsUp className="h-4 w-4 mr-1" />
                            <span>{suggestion.upvotes || 0}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "down")}
                            className={suggestion.userVote === "down" ? "bg-red-50" : ""}
                          >
                            <ThumbsDown className="h-4 w-4 mr-1" />
                            <span>{suggestion.downvotes || 0}</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="discussion">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
                {comments.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No comments yet. Start the discussion!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3 p-3 rounded-lg bg-gray-50">
                      <Avatar>
                        <AvatarImage src={comment.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{comment.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{comment.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-2">
                <Textarea
                  placeholder="Add to the discussion..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex justify-end">
                  <Button onClick={handleSubmitComment}>
                    <MessageSquare className="mr-2 h-4 w-4" /> Post Comment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default MaterialsResourcesModule
